import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { MODEL } from '../shared/generation';
import { countLines } from './code';
import { EXAMPLES } from './examples';
import { firstTokenAt, type Run } from './run';

const fmtInt = (n: number) => n.toLocaleString('en-US');
const s1 = (ms: number) => (ms / 1000).toFixed(1);
const s2 = (ms: number) => (ms / 1000).toFixed(2);

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ACTIVE: ReadonlySet<Run['phase']> = new Set(['requesting', 'streaming', 'compiling']);
const SETTLED: ReadonlySet<Run['phase']> = new Set([
  'rendered',
  'render-failed',
  'render-stalled',
  'incomplete',
  'request-failed',
  'stream-failed',
]);

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    if (!active) return;
    const tick = () => setNow(performance.now());
    tick();
    const timer = window.setInterval(tick, 100);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

type SegStatus = 'pending' | 'active' | 'done' | 'failed';

interface Seg {
  id: string;
  status: SegStatus;
  label: string;
  value?: ReactNode;
}

function Num({ children, width }: { children: ReactNode; width: number }) {
  return (
    <span className="inline-block text-right tabular-nums" style={{ minWidth: `${width}ch` }}>
      {children}
    </span>
  );
}

function Unit({ long, short }: { long: string; short: string }) {
  return (
    <>
      <span className="max-sm:hidden">{long}</span>
      <span className="sm:hidden">{short}</span>
    </>
  );
}

function elapsed(run: Run, now: number): number {
  const observed = [run.respondedAt, run.texts[run.texts.length - 1]?.t ?? null, run.streamEndedAt].filter((v) => v !== null);
  return Math.max(now - run.startedAt, 0, ...observed);
}

function segments(run: Run, now: number): Seg[] {
  const t = elapsed(run, now);
  const ttft = firstTokenAt(run);
  const lines = countLines(run.code);
  const chars = run.code.length;
  const streaming = run.phase === 'streaming' && ttft !== null;

  const first: Seg =
    run.replay !== null
      ? { id: 'origin', status: 'done', label: 'REPLAY' }
      : {
          id: 'origin',
          status: run.phase === 'requesting' ? 'active' : run.phase === 'request-failed' ? 'failed' : 'done',
          label: 'REQUESTED',
        };

  const token: Seg =
    ttft !== null
      ? { id: 'token', status: 'done', label: 'FIRST TOKEN', value: <>{s2(ttft)} s</> }
      : run.phase === 'streaming'
        ? { id: 'token', status: 'active', label: 'FIRST TOKEN', value: <><Num width={5}>{s2(t)}</Num> s</> }
        : { id: 'token', status: run.phase === 'stream-failed' ? 'failed' : 'pending', label: 'FIRST TOKEN' };

  const streamValue = (at: number) => (
    <>
      <Num width={5}>{fmtInt(chars)}</Num> <Unit long="chars" short="ch" /> · <Num width={3}>{lines}</Num>{' '}
      <Unit long="lines" short="ln" /> · <Num width={4}>{s1(at)}</Num> s
    </>
  );
  let stream: Seg;
  if (streaming) stream = { id: 'stream', status: 'active', label: 'STREAMING', value: streamValue(t) };
  else if (run.phase === 'incomplete')
    stream = { id: 'stream', status: 'failed', label: 'STOPPED', value: <>max_tokens · {lines} <Unit long="lines" short="ln" /></> };
  else if (run.phase === 'stream-failed')
    stream = { id: 'stream', status: 'failed', label: run.failure?.label ?? 'FAILED', value: run.failure?.code ?? '—' };
  else if (run.streamEndedAt !== null) stream = { id: 'stream', status: 'done', label: 'STREAMING', value: streamValue(run.streamEndedAt) };
  else stream = { id: 'stream', status: 'pending', label: 'STREAMING' };

  let compile: Seg;
  if (run.phase === 'compiling' && run.streamEndedAt !== null)
    compile = { id: 'compile', status: 'active', label: 'COMPILING', value: <><Num width={4}>{s2(t - run.streamEndedAt)}</Num> s</> };
  else if (run.phase === 'rendered' && run.settledAt !== null && run.streamEndedAt !== null)
    compile = { id: 'compile', status: 'done', label: 'COMPILING', value: <span className="max-sm:hidden">{s2(run.settledAt - run.streamEndedAt)} s</span> };
  else if (run.phase === 'render-failed') compile = { id: 'compile', status: 'failed', label: 'COMPILE FAILED' };
  else if (run.phase === 'render-stalled') compile = { id: 'compile', status: 'failed', label: 'NO ANSWER' };
  else compile = { id: 'compile', status: 'pending', label: 'COMPILING' };

  const rendered: Seg =
    run.phase === 'rendered'
      ? { id: 'rendered', status: 'done', label: 'RENDERED' }
      : run.phase === 'render-failed'
        ? { id: 'rendered', status: 'failed', label: 'EXIT 1' }
        : { id: 'rendered', status: 'pending', label: 'RENDERED' };

  return [first, token, stream, compile, rendered];
}

const SEG_TONE: Record<SegStatus, string> = {
  pending: 'text-white/25 max-sm:hidden',
  active: 'seg-active text-white/95',
  done: 'text-white/55',
  failed: 'text-red-300',
};

function RunLine({ run, now }: { run: Run; now: number }) {
  return (
    <div className="flex min-w-0 flex-1 items-baseline gap-x-2.5 gap-y-[3px] py-2 max-sm:flex-wrap max-sm:gap-x-2 sm:whitespace-nowrap">
      {segments(run, now).map((seg, i) => (
        <span key={seg.id} className={`relative inline-flex items-baseline gap-2.5 max-sm:gap-2 ${SEG_TONE[seg.status]}`}>
          {i > 0 && (
            <span aria-hidden="true" className="text-white/20">
              →
            </span>
          )}
          <span className="seg-lbl relative inline-flex items-baseline gap-1.5 whitespace-nowrap">
            <span>{seg.label}</span>
            {seg.value !== undefined && <span>{seg.value}</span>}
          </span>
        </span>
      ))}
    </div>
  );
}

function Stamp({ run, animate }: { run: Run; animate: boolean }) {
  const lines = countLines(run.code);
  const total = run.settledAt;
  const sep = <span aria-hidden="true">·</span>;
  let body: ReactNode;
  switch (run.phase) {
    case 'rendered':
      body = (
        <>
          <b className="font-medium text-white/92">RENDER OK</b>
          {sep}
          <span className="font-semibold text-fuchsia-400">EXIT 0</span>
          {sep}
          <span>{lines} lines</span>
          {sep}
          <span>{total === null ? '—' : `${s1(total)} s`}</span>
        </>
      );
      break;
    case 'render-failed':
      body = (
        <>
          <b className="font-medium text-white/92">RENDER FAILED</b>
          {sep}
          <span className="font-semibold text-red-300">EXIT 1</span>
          {sep}
          <span className="stamp-msg min-w-0 shrink truncate tracking-normal text-white/62" title={run.compileError?.headline}>
            {run.compileError?.headline ?? '—'}
          </span>
        </>
      );
      break;
    case 'incomplete':
      body = (
        <>
          <b className="font-medium text-white/92">INCOMPLETE</b>
          {sep}
          <span>max_tokens</span>
          <span className="text-white/40">
            · {lines} lines kept · {total === null ? '—' : `${s1(total)} s`}
          </span>
        </>
      );
      break;
    default: {
      const failure = run.failure;
      body = (
        <>
          <b className="font-medium text-white/92">{failure?.label ?? 'FAILED'}</b>
          {sep}
          <span className="font-semibold text-red-300">{failure?.code ?? '—'}</span>
          {sep}
          <span className="stamp-msg min-w-0 shrink truncate tracking-normal text-white/62" title={failure?.message}>
            {failure?.message ?? '—'}
          </span>
        </>
      );
    }
  }
  return (
    <div className="flex min-w-0 flex-1 py-2">
      <p className={`stamp flex min-w-0 items-baseline gap-2 text-white/55 max-sm:flex-wrap max-sm:gap-y-0.5 [&>*]:shrink-0 ${animate ? 'stamp-in' : ''}`}>
        {body}
      </p>
    </div>
  );
}

function speech(run: Run): string {
  const lines = countLines(run.code);
  switch (run.phase) {
    case 'rendered':
      return `Render OK, exit 0, ${lines} lines${run.settledAt === null ? '' : `, ${s1(run.settledAt)} seconds`}.`;
    case 'render-failed':
      return `Render failed, exit 1. ${run.compileError?.headline ?? ''}`;
    case 'incomplete':
      return `Incomplete: max_tokens. ${lines} lines kept.`;
    default:
      return `${run.failure?.label ?? 'Failed'}: ${run.failure?.message ?? ''}`;
  }
}

function announce(run: Run): string {
  switch (run.phase) {
    case 'loaded':
      return '';
    case 'requesting':
      return 'Requesting a live generation.';
    case 'streaming': {
      const ttft = firstTokenAt(run);
      if (ttft === null) return run.replay === null ? 'Waiting for the first token.' : 'Replaying a recording.';
      return `First token after ${s2(ttft)} seconds. Streaming.`;
    }
    case 'compiling':
      return 'Compiling.';
    default:
      return speech(run);
  }
}

interface RailProps {
  run: Run | null;
}

/** The build pipeline: READY at rest, the live stages during a run, then a frozen stamp. */
export function Rail({ run }: RailProps) {
  const active = run !== null && ACTIVE.has(run.phase);
  const settled = run !== null && SETTLED.has(run.phase);
  const now = useNow(active);
  const [stampedId, setStampedId] = useState<number | null>(null);
  const [animatedId, setAnimatedId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const receiptId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const receiptRef = useRef<HTMLDivElement>(null);

  const runId = run?.id ?? null;
  useEffect(() => {
    if (!settled || runId === null) return;
    const reduce = reducedMotion();
    const timer = window.setTimeout(() => {
      setStampedId(runId);
      setAnimatedId(reduce ? null : runId);
    }, reduce ? 0 : 700);
    return () => window.clearTimeout(timer);
  }, [settled, runId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (receiptRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  let line: ReactNode;
  if (run === null || run.phase === 'loaded') {
    line = (
      <p className="flex min-w-0 flex-1 items-baseline gap-2.5 truncate py-2">
        <b className="font-medium text-white/72">READY</b>
        <span aria-hidden="true" className="text-white/25">
          ·
        </span>
        <span className="tracking-[0.02em] text-white/45">{MODEL}</span>
      </p>
    );
  } else if (settled && stampedId === run.id) {
    line = <Stamp run={run} animate={animatedId === run.id} />;
  } else {
    line = <RunLine run={run} now={now} />;
  }

  return (
    <div
      role="group"
      aria-label="Build pipeline"
      className="relative flex min-h-8 items-center gap-3 border-b border-white/10 bg-[#0c0c11] pr-2 pl-5 font-mono text-[11px] leading-4 tracking-[0.06em] text-white/50 max-sm:items-start max-sm:pr-1 max-sm:pl-4 max-sm:text-[10.5px] max-sm:tracking-[0.02em]"
    >
      {line}
      {run !== null && (
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={open}
          aria-controls={receiptId}
          aria-label="Receipt"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex shrink-0 items-center gap-1.5 self-center rounded px-2 py-1 text-[10.5px] font-medium tracking-[0.1em] text-white/50 uppercase transition-colors hover:bg-white/[0.06] hover:text-white aria-expanded:bg-white/[0.06] aria-expanded:text-white max-sm:mt-1 max-sm:self-start"
        >
          <span className="max-sm:hidden">Receipt</span>
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
            className={open ? 'rotate-180' : ''}
          >
            <path d="M2 3.5 5 6.5 8 3.5" />
          </svg>
        </button>
      )}
      {run !== null && open && (
        <div ref={receiptRef} id={receiptId} className="receipt">
          <Receipt run={run} now={now} />
        </div>
      )}
      <p className="sr-only" aria-live="polite">
        {run === null ? '' : announce(run)}
      </p>
    </div>
  );
}

function recordedDate(iso: string): string {
  return iso.slice(0, 10);
}

function Receipt({ run, now }: { run: Run; now: number }) {
  const running = ACTIVE.has(run.phase);
  const loaded = run.phase === 'loaded';
  const replay = run.replay;
  const ttft = firstTokenAt(run);
  const clock = loaded ? 'recorded' : replay !== null ? 'replay clock' : 'client clock';
  const pending = running ? '…' : '—';
  const chip = replay?.chip === null || replay === null ? null : (EXAMPLES.find((e) => e.id === replay.chip)?.label ?? null);
  const title = chip ?? run.prompt;

  let render: { value: string; tone: string };
  if (run.renderOk === true) render = { value: 'EXIT 0', tone: '' };
  else if (run.renderOk === false) render = { value: 'EXIT 1', tone: 'text-red-300' };
  else if (run.silence !== null)
    render = {
      value: run.silence.reason === 'timeout' ? 'bundler timeout' : `no answer in ${s1(run.silence.waited)} s`,
      tone: 'text-red-300',
    };
  else if (run.phase === 'incomplete') render = { value: 'not compiled', tone: 'text-white/50' };
  else if (run.phase === 'request-failed' || run.phase === 'stream-failed') render = { value: 'not compiled', tone: 'text-white/50' };
  else render = { value: pending, tone: running ? '' : 'text-white/50' };

  const stop = run.final?.stop_reason;
  const rows: Array<[label: string, value: string, source: string, tone?: string]> = [
    [
      'model',
      run.final?.model ?? replay?.model ?? MODEL,
      run.final !== null ? 'final event' : replay !== null ? 'recording' : 'shared/generation.ts',
    ],
    ['first token', ttft !== null ? `${s2(ttft)} s` : running ? `${s2(elapsed(run, now))} s …` : '—', clock, ttft === null && !running ? 'text-white/50' : ''],
    ['total', loaded ? '—' : run.settledAt !== null ? `${s2(run.settledAt)} s` : pending, loaded ? 'not run in this view' : clock, run.settledAt === null ? 'text-white/50' : ''],
    ['finish reason', stop === undefined ? pending : (stop ?? '—'), 'final event', stop === 'max_tokens' ? 'text-red-300' : stop == null ? 'text-white/50' : ''],
    ['output tokens', run.final !== null ? fmtInt(run.final.output_tokens) : pending, 'final event', run.final === null ? 'text-white/50' : ''],
    ['text deltas', fmtInt(run.texts.length), 'counted'],
    ['characters', fmtInt(run.code.length), 'counted'],
    ['lines', fmtInt(countLines(run.code)), 'counted'],
    ['render', render.value, 'bundler', render.tone],
    ['this view', replay !== null ? '$0' : 'live request', replay !== null ? 'no request' : '/api/generate'],
  ];

  return (
    <>
      <div className="flex items-baseline justify-between gap-3 text-[10.5px] tracking-[0.14em] text-white/45 uppercase">
        <span>Receipt</span>
        <span className="tracking-[0.04em] normal-case">
          {replay !== null ? `replay · recorded ${recordedDate(replay.recordedAt)}` : 'live run'}
        </span>
      </div>
      <p className="mt-1 mb-2.5 truncate font-sans text-[13px] leading-[18px] font-semibold text-white">{title}</p>
      <table className="w-full border-collapse">
        <tbody>
          {rows.map(([label, value, source, tone]) => (
            <tr key={label}>
              <th scope="row" className="py-[3px] pr-3 text-left align-baseline font-normal whitespace-nowrap text-white/45">
                {label}
              </th>
              <td className={`py-[3px] pr-3 align-baseline whitespace-nowrap text-white tabular-nums ${tone ?? ''}`}>{value}</td>
              <td className="py-[3px] text-right align-baseline text-[11px] text-white/32">{source}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2.5 border-t border-dashed border-white/12 pt-2.5 font-sans text-[11.5px] leading-[17px] text-white/50">
        {replay !== null
          ? 'A genuine run, captured once and replayed through the same stream and compile path. Nothing here was sent to Claude.'
          : 'Measured on this device from the live stream, its final event and the bundler. A dash means nothing reported that value.'}
      </p>
    </>
  );
}
